<template>
  <!-- How personal data reaches a third party and where it goes from there (US 9371). Built from existing links only. -->
  <section class="v2-card df-card" qid="vendor-data-flow">
    <div class="sp-sec__head df-head">
      <font-awesome-icon icon="project-diagram"/>
      <div class="df-head__titles">
        <h3>{{ $t('vendor_flow.title', { name: vendor.name }) }}</h3>
        <span class="v2-card__sub">{{ $t('vendor_flow.subtitle') }}</span>
      </div>
      <span class="sp-grow"></span>
      <div v-if="flow && flow.contracts.length > 1" class="v2-seg" role="tablist">
        <span role="tab" :class="{ on: !contractId }" qid="flow-contract-all" @click="setContract(null)">{{ $t('vendor_flow.all_contracts') }}</span>
        <span v-for="contract in flow.contracts"
              :key="contract.id"
              role="tab"
              :class="{ on: contractId === contract.id }"
              :qid="'flow-contract-' + contract.id"
              @click="setContract(contract.id)">{{ contract.name | truncate(24) }}</span>
      </div>
      <div class="v2-seg" role="tablist">
        <span role="tab" :class="{ on: !sensitiveOnly }" @click="sensitiveOnly = false; selected = null">{{ $t('vendor_flow.all_data') }}</span>
        <span role="tab" :class="{ on: sensitiveOnly }" qid="flow-sensitive" @click="sensitiveOnly = true; selected = null">{{ $t('vendor_flow.sensitive_only') }}</span>
      </div>
    </div>

    <div v-if="!flow" class="home-empty">{{ $t('vendor_list.loading') }}</div>
    <div v-else-if="!visible.links.length" class="home-empty">{{ $t('vendor_flow.empty') }}</div>
    <template v-else>
      <div ref="canvas" class="df-canvas" :style="{ height: height + 'px' }" @click.self="selected = null">
        <svg class="df-links" :width="width" :height="height" aria-hidden="true">
          <path v-for="link in drawnLinks"
                :key="link.key"
                :d="link.d"
                class="df-link"
                :class="{ 'is-on': link.on, 'is-off': link.off }"/>
        </svg>
        <div v-for="(column, index) in COLUMNS"
             :key="column"
             class="df-colhead"
             :style="{ left: columnX(index) + 'px', width: nodeWidth + 'px' }">
          <font-awesome-icon :icon="ICONS[column]"/>{{ $t('vendor_flow.columns.' + column) }}
        </div>
        <button v-for="node in placedNodes"
                :key="node.id"
                type="button"
                class="df-node"
                :class="['is-' + node.type, { 'is-on': isOn(node), 'is-off': isOff(node), 'is-more': node.more, 'is-warn': needsBasis(node) }]"
                :style="{ left: node.x + 'px', top: node.y + 'px', width: nodeWidth + 'px' }"
                :title="node.name"
                :qid="'flow-node-' + node.id"
                @click="select(node)">
          <span class="df-node__icon"><font-awesome-icon :icon="ICONS[node.type]"/></span>
          <span class="df-node__text">
            <b>{{ node.name }}</b>
            <small v-if="subline(node)">{{ subline(node) }}</small>
          </span>
          <span v-if="needsBasis(node)" class="df-basis is-none">{{ $t('vendor_flow.no_basis') }}</span>
        </button>
      </div>

      <!-- The traced route -->
      <div v-if="selectedNode" class="df-route" qid="flow-route">
        <font-awesome-icon :icon="ICONS[selectedNode.type]"/>
        <div class="df-route__text">
          <b>{{ selectedNode.name }}</b>
          <span>{{ routeText }}</span>
        </div>
        <router-link v-if="routeFor(selectedNode)" :to="routeFor(selectedNode)" class="v2-link">{{ $t('systems_overview.row.open') }} <font-awesome-icon icon="arrow-right"/></router-link>
        <button type="button" class="v2-link" @click="selected = null">{{ $t('vendor_flow.clear') }}</button>
      </div>
      <div v-else class="df-hint">{{ $t('vendor_flow.hint') }}</div>
    </template>
  </section>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import {isValidBasis, basisLabel} from '@/util/transferBasis.js';

  const COLUMNS = ['activity', 'system', 'contract', 'third_party', 'sub_processor', 'country'];
  const ICONS = { activity: 'project-diagram', system: 'desktop', contract: 'file-alt', third_party: 'building', sub_processor: 'users', country: 'globe' };
  // Rows per column before the rest are folded into "+N more"
  const MAX_ROWS = 9;
  const ROW = 50;
  const TOP = 34;
  const NODE_HEIGHT = 40;

  export default {
    name: 'VendorDataFlowTab',
    props: {
      vendor: { type: Object, required: true }
    },
    data() {
      return {
        COLUMNS: COLUMNS,
        ICONS: ICONS,
        flow: null,
        contractId: null,
        sensitiveOnly: false,
        selected: null,
        width: 1000
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      nodeWidth() {
        return Math.max(130, Math.min(200, (this.width - 5 * 24) / 6));
      },
      nodesById() {
        let map = {};
        (this.flow ? this.flow.nodes : []).forEach(node => {
          map[node.id] = node;
        });
        return map;
      },
      // Links of the chosen contract, and only from sensitive activities when asked
      visible() {
        if (!this.flow) {
          return { links: [], nodes: [] };
        }
        let nodes = this.nodesById;
        let links = this.flow.links.filter(link => !this.contractId || link.contracts.includes(this.contractId));
        if (this.sensitiveOnly) {
          links = links.filter(link => nodes[link.from].type !== 'activity' || nodes[link.from].sensitive);
          // Systems no sensitive activity uses carry nothing sensitive
          let fed = {};
          links.forEach(link => {
            if (nodes[link.from].type === 'activity') {
              fed[link.to] = true;
            }
          });
          links = links.filter(link => nodes[link.from].type !== 'system' || fed[link.from]);
        }
        let ids = {};
        links.forEach(link => {
          ids[link.from] = true;
          ids[link.to] = true;
        });
        return { links: links, nodes: Object.keys(ids).map(id => nodes[id]) };
      },
      // Nodes with their place: one column per type, sorted by name, the rest folded into "+N more"
      placedNodes() {
        let placed = [];
        COLUMNS.forEach((column, index) => {
          let items = this.visible.nodes.filter(node => node.type === column)
            .sort((a, b) => a.name.localeCompare(b.name));
          let shown = items;
          if (items.length > MAX_ROWS) {
            // The traced node always stays visible
            shown = items.slice(0, MAX_ROWS - 1);
            let chosen = items.find(node => node.id === this.selected);
            if (chosen && !shown.includes(chosen)) {
              shown[shown.length - 1] = chosen;
            }
            shown = shown.concat([{ id: 'more-' + column, type: column, more: true, name: this.$t('vendor_flow.more', { count: items.length - shown.length }) }]);
          }
          shown.forEach((node, row) => {
            placed.push(Object.assign({}, node, { x: this.columnX(index), y: TOP + row * ROW }));
          });
        });
        return placed;
      },
      placedById() {
        let map = {};
        this.placedNodes.forEach(node => {
          map[node.id] = node;
        });
        return map;
      },
      height() {
        let rows = Math.max(1, ...COLUMNS.map(column => this.placedNodes.filter(node => node.type === column).length));
        return TOP + rows * ROW;
      },
      // Everything up- and downstream of the traced node
      routeIds() {
        if (!this.selected) {
          return null;
        }
        let links = this.visible.links;
        let ids = {};
        ids[this.selected] = true;
        let walk = (start, forward) => {
          let queue = [start];
          while (queue.length) {
            let id = queue.shift();
            links.forEach(link => {
              let from = forward ? link.from : link.to;
              let to = forward ? link.to : link.from;
              if (from === id && !ids[to + (forward ? '>' : '<')]) {
                ids[to + (forward ? '>' : '<')] = true;
                ids[to] = true;
                queue.push(to);
              }
            });
          }
        };
        walk(this.selected, true);
        walk(this.selected, false);
        return ids;
      },
      drawnLinks() {
        let half = NODE_HEIGHT / 2;
        let width = this.nodeWidth;
        return this.visible.links.map(link => {
          let from = this.placedById[link.from];
          let to = this.placedById[link.to];
          if (!from || !to) {
            return null;
          }
          let x1 = from.x + width;
          let y1 = from.y + half;
          let x2 = to.x;
          let y2 = to.y + half;
          let mx = (x1 + x2) / 2;
          let on = this.routeIds && this.routeIds[link.from] && this.routeIds[link.to];
          return {
            key: link.from + '>' + link.to,
            d: 'M' + x1 + ' ' + y1 + ' C' + mx + ' ' + y1 + ', ' + mx + ' ' + y2 + ', ' + x2 + ' ' + y2,
            on: !!on,
            off: !!this.routeIds && !on
          };
        }).filter(Boolean);
      },
      selectedNode() {
        return this.selected ? this.nodesById[this.selected] : null;
      },
      // What the traced node is connected to, in a sentence
      routeText() {
        let ids = this.routeIds || {};
        // Upstream (where the data comes from) and downstream (where it goes)
        let up = type => this.visible.nodes.filter(node => node.type === type && ids[node.id + '<']);
        let of = type => this.visible.nodes.filter(node => node.type === type && ids[node.id + '>']);
        let countries = of('country');
        let outside = countries.filter(node => node.eea === false);
        let parts = [];
        let activities = up('activity').length;
        let systems = up('system').length;
        let sources = [];
        if (activities) {
          sources.push(this.$tc('contract_page.short.activities', activities, { count: activities }));
        }
        if (systems) {
          sources.push(this.$tc('contract_page.short.systems', systems, { count: systems }));
        }
        if (sources.length) {
          parts.push(this.$t('vendor_flow.route.from', { list: sources.join(', ') }));
        }
        if (countries.length) {
          parts.push(this.$t('vendor_flow.route.reaches', { list: countries.map(node => node.name).join(', ') }));
        }
        let missing = outside.filter(node => this.needsBasis(node));
        if (missing.length) {
          parts.push(this.$t('vendor_flow.route.no_basis', { list: missing.map(node => node.name).join(', ') }));
        }
        return parts.join(' ');
      }
    },
    watch: {
      'vendor.id': function () {
        this.load();
      }
    },
    methods: {
      load() {
        let self = this;
        piincHttp.get('vendors/' + this.vendor.slug + '/data-flow', { params: { client: this.getClient.slug } }).then(function(response) {
          self.flow = response.data;
          self.$nextTick(self.measure);
        }, function() {});
      },
      measure() {
        let canvas = this.$refs.canvas || this.$el;
        if (canvas && canvas.clientWidth) {
          this.width = canvas.clientWidth;
        }
      },
      columnX(index) {
        return Math.round(index * (this.width - this.nodeWidth) / (COLUMNS.length - 1));
      },
      setContract(id) {
        this.contractId = id;
        this.selected = null;
      },
      select(node) {
        if (node.more) {
          return;
        }
        this.selected = this.selected === node.id ? null : node.id;
      },
      isOn(node) {
        return !!this.routeIds && !!this.routeIds[node.id];
      },
      isOff(node) {
        return !!this.routeIds && !this.routeIds[node.id];
      },
      // Outside the EU/EEA and a contract that sends data there has no valid basis (US 9371)
      needsBasis(node) {
        return node.type === 'country' && node.eea === false && !!node.missing_basis;
      },
      basisShort(node) {
        if (this.needsBasis(node)) {
          return this.$t('vendor_flow.no_basis');
        }
        return node.basis.filter(isValidBasis).map(basis => basisLabel(this, basis)).join(', ');
      },
      subline(node) {
        if (node.more) {
          return '';
        }
        if (node.type === 'system' && node.runs_as) {
          return this.$t('systems_overview.runs_as.' + node.runs_as);
        }
        if (node.type === 'activity' && node.sensitive) {
          return this.$t('vendor_flow.sensitive');
        }
        if (node.type === 'contract') {
          return node.dpa === 'Yes' ? this.$t('contract_hero.dpa') : '';
        }
        if (node.type === 'country') {
          if (node.eea) {
            return this.$t('vendor_page.data.eea');
          }
          return node.eea === false && !this.needsBasis(node) ? this.basisShort(node) : '';
        }
        return '';
      },
      routeFor(node) {
        let names = { activity: 'activities.view', system: 'systems.view', contract: 'contracts.view', third_party: 'contracts.third_parties.view', sub_processor: 'contracts.third_parties.view' };
        return names[node.type] && node.slug ? { name: names[node.type], params: { slug: node.slug } } : null;
      }
    },
    created() {
      this.load();
    },
    mounted() {
      window.addEventListener('resize', this.measure);
    },
    beforeDestroy() {
      window.removeEventListener('resize', this.measure);
    }
  }
</script>
